import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, Wallet, FolderLock, Users, Newspaper, Compass, MessagesSquare,
  TowerControl, Smartphone, Search, ChevronRight, LayoutGrid,
} from "lucide-react";
import UnsplashHero from "../components/UnsplashHero";

/* /features — living catalog of everything built into TravelerPulse.pro. */

const GROUPS = [
  {
    id: "hub", icon: Briefcase, tone: "text-sky-700 bg-sky-50 border-sky-200",
    title: "My Travel Hub — trip planning core",
    items: [
      ["Trips & Timeline", "/travel-hub", "Every trip with segments, layovers, PNRs and a day-by-day timeline"],
      ["AI Trip Planner", "/plan-ai", "A complete day-by-day plan for any destination in seconds"],
      ["Group Trip Planner", "/grouptrip-planner", "Plan together — invite your Travel Circle with view or edit roles"],
      ["Trip Today", "/trip-today", "Your live travel-day view: what's next, gates, times and tips"],
      ["Traveler Diary & Journal", "/travel-hub", "Collaborative trip diary with live co-editing for shared trips"],
      ["Trip Group Chat", "/travel-hub", "Private chat thread for everyone on a shared trip, with alerts"],
      ["Packing Lists", "/packing", "Smart packing lists per trip, plus a general packing guide"],
      ["Trip Album & Memoirs", "/memoirs", "Photo album per trip and shareable travel memoirs"],
      ["Flight Status & Alerts", "/flight-search", "Smart flight search with saved-flight status checks every 30 minutes"],
      ["Trip Countdown Widget", "/travel-hub", "Shareable countdown you can embed anywhere"],
      ["Travel Reminders", "/remind-me", "Visa, check-in and document reminders that email you in time"],
      ["Travel Gear", "/gear", "Track your bags and gear, link them to trips and family members"],
    ],
  },
  {
    id: "money", icon: Wallet, tone: "text-emerald-700 bg-emerald-50 border-emerald-200",
    title: "Expenses & Money",
    items: [
      ["Trip Expense Manager", "/trip-expense-manager", "Per-trip spending with budgets, receipts (OCR) and category donuts"],
      ["Everyday Expense Manager", "/expenses", "Personal & household spending across 17 categories, month by month"],
      ["Family Expense Hub", "/family-expense-hub", "Everyone logs under their own name; who-owes-whom settlement"],
      ["Settle Up + WhatsApp Share", "/family-expense-hub", "Minimal-transfer settlement suggestions, shareable to WhatsApp"],
      ["Receipt Gallery", "/travel-hub", "Browse and download every receipt captured on a trip"],
      ["AI Spending Insights", "/expenses", "Savings opportunities spotted in your spending patterns"],
      ["Monthly Spend Recap", "/expenses", "A recap email on the 1st of every month"],
      ["Price Watch & Deal Alerts", "/price-watch", "Watch fares and get alerted when prices drop"],
      ["Airfare Explorer", "/fares", "Typical fares by route to sanity-check any quote"],
    ],
  },
  {
    id: "docs", icon: FolderLock, tone: "text-violet-700 bg-violet-50 border-violet-200",
    title: "Documents & Security",
    items: [
      ["Document Locker", "/locker", "Passports, visas, tickets and IDs in an encrypted vault"],
      ["Locker Sharing", "/locker", "Share documents with family; expiring share links for anyone else"],
      ["Step-Up Protection", "/privacy-security", "Extra verification for your most sensitive (L4) documents"],
      ["Privacy & Security Center", "/privacy-security", "MFA, sessions, data export and privacy controls in one place"],
      ["Account Recovery", "/recovery", "Recover access safely if you lose your device"],
    ],
  },
  {
    id: "family", icon: Users, tone: "text-rose-700 bg-rose-50 border-rose-200",
    title: "Family Space",
    items: [
      ["Household Sharing", "/travel-hub", "One household — shared trips, expenses, lockers and lists"],
      ["Travel Circle Invites", "/travel-hub", "Email invites with roles and access expiry for trip collaborators"],
      ["Family Trip Collaboration", "/travel-hub", "Co-edit diaries, split expenses and chat on shared trips"],
      ["Travel Diaries", "/travel-diaries", "Family travel stories, together in one place"],
    ],
  },
  {
    id: "pulse", icon: Newspaper, tone: "text-amber-700 bg-amber-50 border-amber-200",
    title: "My Social Pulse — daily life",
    items: [
      ["Daily Briefing", "/my-social-pulse", "Your morning pulse: news, weather and what matters today"],
      ["Food Pulse", "/my-social-pulse/food", "Meal ideas, recipes and food lists you can share"],
      ["Pantry & Expiry Meal Ideas", "/my-social-pulse/pantry", "Track the pantry; AI suggests meals before food expires"],
      ["Recipe → Shopping List", "/my-social-pulse/food", "Turn any recipe into a shopping list in one tap"],
      ["Weekly Digest", "/my-social-pulse", "A weekly email digest on the day you choose"],
      ["Partner News Feeds", "/my-social-pulse", "Curated sources feeding your personal pulse"],
    ],
  },
  {
    id: "explore", icon: Compass, tone: "text-blue-700 bg-blue-50 border-blue-200",
    title: "Explore & Research",
    items: [
      ["Airports Encyclopedia", "/airports", "Directories, rankings and detailed profiles worldwide"],
      ["Airlines Encyclopedia", "/airlines", "Airline profiles, rankings and route knowledge"],
      ["Visas & Embassies", "/visas", "Visa requirements and Indian & world missions, searchable"],
      ["Countries & Cities", "/countries", "Country profiles, city guides and world city collections"],
      ["Destinations", "/destinations", "Curated destination guides and top-destination picks"],
      ["Interactive Map", "/map", "Explore hubs, routes and places visually"],
      ["Aircraft & Manufacturers", "/aircraft", "Know the plane you're flying on"],
      ["Travel News", "/travel-news", "Aviation and travel headlines, always fresh"],
      ["Guides & Articles", "/guides", "In-depth travel guides, specials and AI-assisted articles"],
      ["Baggage Rules", "/baggage", "Allowances and restrictions by airline"],
      ["Pet Travel", "/pet-travel", "Fly safely with your pets"],
      ["Rail, Bus, Road & Water", "/rail", "Beyond flying: stations, operators and ground transport"],
      ["Hotels & Stays", "/stays", "Places to stay near where you're going"],
      ["Loyalty Programs", "/loyalty", "Track programs and make points work harder"],
    ],
  },
  {
    id: "community", icon: MessagesSquare, tone: "text-teal-700 bg-teal-50 border-teal-200",
    title: "Community & Marketplace",
    items: [
      ["Traveler Community", "/community", "Discussions, photos and tips from real travelers"],
      ["Complaints Desk", "/complaints", "Raise and track airline & airport complaints"],
      ["Marketplace & Businesses", "/marketplace", "Find and list travel businesses; claim your profile"],
      ["Officials Program", "/officials/apply", "Verified airport & airline officials answering travelers"],
      ["Feedback & Support", "/support", "We read everything — tell us what to build next"],
    ],
  },
  {
    id: "india", icon: TowerControl, tone: "text-orange-700 bg-orange-50 border-orange-200",
    title: "IndianAirports.com portal",
    items: [
      ["Airport Directory (44)", "/india/airports/directory", "Every Indian airport with terminals, transport and tips"],
      ["Airport Portals", "/india/airports", "Deep profiles: terminal guides, routes, FAQs, Ask Airport AI"],
      ["My Flight", "/india/my-flight", "Your personalized travel-day page from a flight number"],
      ["Structured Reviews + Photos", "/india/airports/kochi-cok", "Rate cleanliness, security, food and more — with photos"],
      ["Routes & States", "/india/airports", "Route guides and state landing pages across India"],
      ["Travel to India Guides", "/india/guides", "Visiting India + OCI & NRI rules, with audio and 10 languages"],
      ["Partner Widget", "/india/partners", "One-line embeddable planner for partner websites"],
      ["About & City Profiles", "/india/about", "The platform story and every airport city, linked"],
    ],
  },
  {
    id: "platform", icon: Smartphone, tone: "text-zinc-700 bg-zinc-100 border-zinc-300",
    title: "Platform & Apps",
    items: [
      ["Install as an App (PWA)", "/get-app", "One-tap install on phone or desktop, with auto-updates"],
      ["Listen (Text-to-Speech)", "/india/guides/visiting-india", "Pages and guides read aloud, section by section"],
      ["Translate (30+ languages)", "/india/guides", "Whole-site translation including 10 Indian languages"],
      ["SocialPulse Sign-In", "/login", "One account across the whole sister-site network"],
      ["Membership", "/membership", "Premium features powered by secure Stripe checkout"],
      ["Push Notifications", "/profile", "Flight alerts and trip updates straight to your device"],
    ],
  },
];

export default function Features() {
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "All Features — TravelerPulse.pro";
    window.scrollTo(0, 0);
  }, []);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return GROUPS;
    return GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(([name, , desc]) => `${name} ${desc}`.toLowerCase().includes(needle)),
    })).filter((g) => g.items.length > 0);
  }, [q]);

  const total = GROUPS.reduce((n, g) => n + g.items.length, 0);

  return (
    <div data-testid="features-page">
      <UnsplashHero
        query="traveler journal map planning trip"
        eyebrow="Feature catalog"
        title="Everything TravelerPulse can do"
        subtitle={`${total} features across ${GROUPS.length} areas — trip planning, money, documents, family, daily life, exploration, community and the IndianAirports.com portal.`}
        testid="features-hero"
      />
      <div className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-5 py-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search features… e.g. expenses, visa, photo"
              data-testid="features-search"
              className="w-full text-sm border border-zinc-300 rounded-full pl-9 pr-4 py-2.5 bg-white focus:outline-none focus:border-sky-400" />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-10 space-y-10">
        {groups.length === 0 && <p className="text-sm text-zinc-500" data-testid="features-empty">No features match "{q}".</p>}
        {groups.map(({ id, icon: Icon, tone, title, items }) => (
          <section key={id} data-testid={`features-group-${id}`}>
            <div className="flex items-center gap-2.5">
              <span className={`inline-flex p-2 rounded-xl border ${tone}`}><Icon className="w-4 h-4" /></span>
              <h2 className="font-heading font-800 text-lg md:text-lg">{title}</h2>
              <span className="text-[11px] font-800 text-zinc-400">{items.length}</span>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map(([name, to, desc]) => (
                <Link key={name} to={to} data-testid={`feature-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group bg-white border border-zinc-200 rounded-2xl p-4 hover:border-sky-400 transition-colors">
                  <div className="flex items-center gap-1 text-sm font-800 text-zinc-800 group-hover:text-sky-700">
                    {name}
                    <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{desc}</p>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
